import {
  generalTerm,
} from './sk.shared.ts';

import {
  TComponentPT9800Keys, TComponentPT9800Value, TComputedPT9800PlannedPartInput,
  Tpt9800TrlTokens,
} from '@/types/openapi-type.ts';

const processFlowManualCommand: TComponentPT9800Keys<'processFlowManualCommand'> = {
  acknowledged: 'Potvrdené',
  additionalInfoColumnCaption: 'Popis doplnkových informácií',
  additionalInfoColumnValue: 'Hodnota doplnkových informácií',
  idFieldName: 'Názov ID poľa',
  idFieldValue: 'Hodnota ID poľa',
  ldArchivCrit1Name: 'Popis Archiv-Crit1',
  ldArchivCrit1Value: 'Hodnota Archiv-Crit1',
  orderNumberCaption: 'Popis čísla objednávky',
  orderNumberValue: 'Hodnota čísla objednávky',
  priority: 'Priorita',
  rowId: 'ID riadku',
  source: 'Zdroj',
  sourceWpId: 'ID zdrojového WP',
  target: 'Cieľ',
  targetWpId: 'ID cieľového WP',
  wpId: 'ID WP vozidla',
};

const recipeRuntimeInfo: TComponentPT9800Keys<'recipeRuntimeInfo'> = {
  actualStepDuration: 'Skutočná dĺžka kroku',
  loadIdentifier: generalTerm.loadIdentifier,
};

const recipeListItem: TComponentPT9800Keys<'recipeListItem'> = {
  id: 'id',
  description: generalTerm.description,
  recipe: generalTerm.recipe,
  recipePrefix: 'Prefix receptu',
  version: 'Verzia receptu',
};

const recipeItemHeader: Partial<TComponentPT9800Keys<'batchRecipeItemHeader'>> = {
  recipeName: 'Názov',
  version: generalTerm.revision,
  recipePrefix: 'Skupina',
  description: 'Popis',
  processType: 'Proces',
  processId: 'Operácia',
  comment: 'Komentár',
  diff: 'Aktivovať',
  correction: 'Oprava',
};

const material: Partial<TComponentPT9800Keys<'material'>> = {
  // Diffusion field labels reuse the shared generalTerm wording so they read identically here, in
  // the Diffusion Inputs panel and in the simulator.
  carbonContent: generalTerm.carbonContent,
  carbideLimit: generalTerm.carbideLimit,
  alloyFactor: generalTerm.alloyFactor,
  customerMaterialId: 'Materiál',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchingIntensity: generalTerm.quenchingIntensity,
  representativeDiameter: generalTerm.representativeDiameter,
  requiredHardness: generalTerm.requiredHardness,
  targetSurfC: generalTerm.targetSurfaceCarbon,
  co: generalTerm.carbonMonoxide,
  h2: generalTerm.hydrogen,
};

const beltRecipeItemHeader: Partial<TComponentPT9800Keys<'beltRecipeItemHeader'>> = {
  description1: 'Popis 1',
  description2: 'Popis 2',
  description3: 'Popis 3',
  description4: 'Popis 4',
  processId: 'Operácia',
  processDepth: 'Hĺbka cementácie',
  processDepthAt: 'Obsah uhlíka v hĺbke cementácie',
};

const pusherRecipeItemHeader: Partial<TComponentPT9800Keys<'pusherRecipeItemHeader'>> = {
  description: beltRecipeItemHeader.description1,
  description1: beltRecipeItemHeader.description2,
  processDepth: beltRecipeItemHeader.processDepth,
  processDepthAt: beltRecipeItemHeader.processDepthAt,
  caseHardeningDepthFrom: 'Hĺbka cementovanej vrstvy od',
  caseHardeningDepthTo: 'Hĺbka cementovanej vrstvy do',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchIntensity: generalTerm.quenchingIntensity,
  partId: generalTerm.part,
};

const recipeItemPosition: Partial<TComponentPT9800Keys<'recipeItemPosition'>> = {
  setpoints: 'Nastavené hodnoty',
  endAfter: 'Koncová podmienka',
  digitalOutputList: 'Digitálne výstupy',
  digitalInputList: 'Digitálne vstupy',
};

const recipeItemPositionHeader: Partial<TComponentPT9800Keys<'recipeItemPositionHeader'>> = {
  positionNo: 'Krok',
  description: 'Názov kroku',
  chamberId: 'Komora',
  chamberOperationId: 'Operácia komory',
  afterId: 'Koncová podmienka',
  estimatedDurationInMin: 'Odhadovaná dĺžka',
  endAtPerc: 'Koniec na %cd',
  afterTimeInMin: 'Trvanie koncovej podmienky',
};

const plannedPartInput: Partial<Record<keyof TComputedPT9800PlannedPartInput, string>> = {
  pos: 'Pozícia',
  partName: 'Číslo dielu',
  partDescription: 'Popis dielu',
  orderNo: generalTerm.loadIdentifier,
  orderRemark: 'Poznámka k objednávke',
  customerOrderNumber: 'Číslo objednávky zákazníka',
  amount: 'Množstvo',
  weight: 'Hmotnosť',
  totalWeight: 'Celková hmotnosť',
};

const processFlowContent: Partial<TComponentPT9800Keys<'processFlowContent'>> = {
  name: 'Č. obsahu',
  weight: 'Hmotnosť PLC',
  createdTimestamp: 'Vytvorené',
  modifiedTimestamp: 'Upravené',
};

const processFlow: Partial<TComponentPT9800Keys<'processFlow'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
  updateTimestamp: 'Posledná aktualizácia',
  automatNo: 'Automat č.',
  comment1: 'Komentár 1',
  comment2: 'Komentár 2',
  comment3: 'Komentár 3',
};

const processFlowListItem: Partial<TComponentPT9800Keys<'processFlowListItem'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
};

const planLoadInput: Partial<TComponentPT9800Keys<'planLoadInput'>> = {
  workplaceWoNumber: generalTerm.loadIdentifier,
};

const setPointItem: Partial<TComponentPT9800Keys<'setPointItem'>> & {
  unit: string,
} = {
  userComponentId: 'Nastavená hodnota',
  controlId: 'Riadiaci kláves',
  value: 'Hodnota',
  unit: 'Jednotka',
  toleranceMax: 'Tol +',
  toleranceMin: 'Tol -',
  alarmStartTimeInMin: 'Štart alarmu',
  alarmToleranceTimeInMin: 'Tolerancia alarmu',
  endControlId: 'Koniec ctrl',
  endValue: 'Koncová podmienka',
};

const furnaceAlarm: Partial<TComponentPT9800Keys<'furnaceAlarm'>> = {
  wpName: 'Pracovisko',
  ackTime: 'Potvrdenie',
  alarmBit: 'Bit',
  alarmByte: 'Bajt',
  endTime: 'Koniec',
  gate: 'Brána',
  alarmNo: 'ID',
  message: 'Správa',
  priority: 'Priorita',
  startTime: 'Štart',
};

const load: Partial<TComponentPT9800Keys<'load'>> = {
  name: generalTerm.jobDisplayName,
  startTime: generalTerm.jobStartedAtUtc,
  endTime: generalTerm.jobFinishedAtUtc,
  identifier: generalTerm.loadIdentifier,
  recipe: generalTerm.recipe,
  partsText: plannedPartInput.partDescription,
  workOrderNo: plannedPartInput.orderNo,
  orderRemark: plannedPartInput.orderRemark,
  status: 'Stav',
  comment: 'Komentár',
  processFlowLoadName: 'Záťaž procesného toku',
  packedTime: 'Čas balenia',
  packedUser: 'Zabalil',
  startedUser: 'Spustil',
  earliestTimeStart: 'Najskorší štart',
};

const loadState: Record<TComponentPT9800Value<'load'>['status'], string> = {
  EXT_START: 'Spustené (externe)',
  FINISHED: 'Dokončené',
  PLANNED: 'Naplánované',
  RUNNING: 'Prebieha',
  RUNNING_LOADING_ENDED: 'Spustené (načítanie ukončené)',
  STARTED: 'Spustené',
  STOPPED: 'Zastavené',
  TOO_OLD_NEVER_STARTED: 'Nikdy nespustené',
  UNKNOWN: 'Neznáme',
  WAITING_FOR_CONDITIONS: 'Čaká na podmienky',
  WAITING_FOR_LOADING: 'Čaká na načítanie',
};

const WorkPlaceStatus: Partial<TComponentPT9800Keys<'WorkPlaceStatus'>> = {
  connected: 'pripojené',
  recipe: 'Aktuálny krok',
  remote: 'vzdialené',
  hold: 'Pozdržať',
};

const WorkPlaceStatusStatus: Record<Exclude<TComponentPT9800Value<'WorkPlaceStatus'>['status'], undefined>, string> = {
  ALARM: 'Alarm',
  IDLE: 'Nečinný',
  NO_REMOTE_CONNECTION: 'Žiadne vzdialené pripojenie',
  RUNNING: 'Spustený recept',
  SWITCHED_OFF: 'Vypnuté',
};

export const pt9800Translations = {
  T_MENU_GROUP_USER: 'Používateľ',
  T_MENU_GROUP_TERMINAL: 'Terminál',
  T_MENU_GROUP_SYSTEM_1: 'Systém-1',
  T_MENU_GROUP_SYSTEM_2: 'Systém-2',
  T_MENU_ITEM_USER_GROUPS: 'Skupiny používateľov',
  T_MENU_ITEM_USERS: 'Používatelia',
  T_MENU_ITEM_APPLICATION_GROUPS: 'Skupiny aplikácií',
  T_MENU_ITEM_APPLICATIONS: 'Aplikácie',
  T_MENU_ITEM_USER_ACCESS: 'Prístup používateľov',
  T_MENU_ITEM_MATERIAL_MANAGEMENT: 'Materiály',
  T_MENU_ITEM_PART_MANAGEMENT: 'Správa dielov',
  T_MENU_ITEM_PART_CUSTOMERS: 'Zákazníci',
  T_MENU_ITEM_TERMINAL_TYPE: 'Typ terminálu',
  T_MENU_ITEM_TERMINAL: 'Terminál',
  T_MENU_ITEM_CLIENT_ACCESS: 'Prístup klienta',
  T_MENU_ITEM_COLOR_ADMINISTRATION: 'Farby',
  T_MENU_ITEM_BOX_ADMINISTRATION: 'Boxy',
  T_MENU_ITEM_ALARMS: 'Alarmy',
  T_MENU_ITEM_BACKGROUND_PROCESSES: 'Procesy na pozadí',
  T_MENU_ITEM_TASKS: 'Úlohy',
  T_MENU_ITEM_WORKPLACES: 'Pracoviská',
  T_MENU_ITEM_OPERATIONS: 'Operácie',
  T_MENU_GROUP_SYSTEM_DATA: 'Systémové údaje',
  T_MENU_GROUP_EVALUATION: 'Hodnotenie',
  T_MENU_GROUP_PART: 'Diel',
  T_MENU_GROUP_SPECIAL: 'Špeciálne',
  T_MENU_ITEM_PRODUCTION_LOGS: 'Výrobné záznamy',
  T_MENU_ITEM_WORKPLACE_TYPE: 'Typ pracoviska',

  T_SHOP_FURNACE_STATUS_NOT_CONNECTED: 'Modrá=Zariadenie nepripojené',
  T_SHOP_FURNACE_STATUS_MANUAL_MODE: 'Biela=Manuálny režim',
  T_SHOP_FURNACE_STATUS_NO_TREATMENT_RUNNING: 'Žltá=Žiadne aktívne tepelné spracovanie',
  T_SHOP_FURNACE_STATUS_PLC_DENIES_START: 'Ružová=PLC odmieta spustenie receptu',
  T_SHOP_FURNACE_STATUS_RUNNING: 'Zelená=Tepelné spracovanie prebieha – žiadne alarmy',
  T_SHOP_FURNACE_STATUS_ALARM: 'Červená=Vyskytujú sa alarmy',
  T_SHOP_FURNACE_STATUS_OFF: 'Svetlosivá=Vypnuté (pozri základné údaje pracoviska)',
  T_SHOP_FURNACE_STATUS_MAINTENANCE: 'Tmavosivá=V režime údržby (pozri základné údaje pracoviska)',
  T_SHOP_FURNACE_STATUS_CONFIG_ERROR: 'Tyrkysová=Chyba konfigurácie',
  T_SHOP_FURNACE_STATUS_ENERGY_MAN_DISABLED_START: 'Ružová=Energetický manažment blokuje spustenie receptu',
  T_SHOP_FURNACE_STATUS_WEEKEND_MODE: 'Víkendový režim',
  T_SHOP_FURNACE_STATUS_NO_ALARMS: 'Zelená=Žiadne alarmy',

  T_SHOP_STATUS_CELL_READY_FOR_WORK: 'Bunka pripravená na prácu',
  T_SHOP_STATUS_CELL_NOT_READY_FOR_WORK: 'Bunka NIE je pripravená na prácu !!!',

  T_SHOP_CAR_STATUS_NO_PLC_SIMULATE: '!!! ŽIADNE PLC ⇒ Simulácia !!!',
  T_SHOP_CAR_STATUS_MANUAL: 'MANUÁLNE',
  T_SHOP_CAR_STATUS_AUTO: 'Auto',
  T_SHOP_CAR_STATUS_SCHED: 'SCHED',
  T_SHOP_CAR_STATUS_NOT_ACTIVE: 'NAKLADACÍ VOZÍK NEAKTÍVNY',
  T_SHOP_CAR_STATUS_LOGIC_NOT_RUNNING: 'Logika neprebieha',
  T_SHOP_CAR_STATUS_NOT_IN_ZERO_POS: 'Nie v nulovej polohe',
  T_SHOP_CAR_STATUS_S_ACTIVE: 'SK aktívny',
  T_SHOP_CAR_STATUS_CRANE: 'Žeriav',
  T_SHOP_CAR_STATUS_S: 'S',
  T_SHOP_CAR_STATUS_T: 'T',
  T_SHOP_CAR_STATUS_CREQ: 'CReq',
  T_SHOP_CAR_STATUS_REQ: 'Req',
  T_SHOP_CAR_STATUS_WANT_CANCEL: 'Požiadavka na zrušenie',
  T_SHOP_CAR_STATUS_CANCEL: 'Zrušiť',
  T_SHOP_CAR_STATUS_NOT_VALID: 'Neplatný',
  T_SHOP_CAR_STATUS_REF: 'Ref.',
  T_SHOP_CAR_STATUS_RUNNING: 'Prebieha',
  T_SHOP_CAR_STATUS_FINISHED: 'Ukončený',
  T_SHOP_CAR_STATUS_WILL_END: 'Bude ukončený',
  T_SHOP_CAR_STATUS_TOS: 'do S',
  T_SHOP_CAR_STATUS_LDS: 'načítaj S',
  T_SHOP_CAR_STATUS_SLD: 'S načítané',
  T_SHOP_CAR_STATUS_TOD: 'do D',
  T_SHOP_CAR_STATUS_DL: 'vyložiť',
  T_SHOP_CAR_STATUS_DLD: 'D vyložené',

  T_SHOP_CAR_CONTROL_CRANE: 'Žeriav v režime SCHED',
  T_SHOP_CAR_1_SCHED_MODE: 'Vozík 1 – režim SCHED',
  T_SHOP_CAR_2_SCHED_MODE: 'Vozík 2 – režim SCHED',
  T_SHOP_CAR_3_SCHED_MODE: 'Vozík 3 – režim SCHED',
  T_SHOP_CAR_4_SCHED_MODE: 'Vozík 4 – režim SCHED',
  T_SHOP_CAR_OIL_QUENCH_SCHEDULED: 'Zakalenie v oleji naplánované',
  T_SHOP_CAR_IN_SCHEDULED_MODE: 'Vozík v naplánovanom režime',
  T_SHOP_CAR_SCHEDULED: 'Naplánované',
  T_SHOP_STORAGE_SCHEDULED: 'Sklad v režime SCHED',

  T_SHOP_PLACE_STATUS_FINISHED: 'Ukončené',
  T_SHOP_PLACE_STATUS_CHECK: 'Skontroluj',
  T_SHOP_PLACE_STATUS_ASAP: 'Urgentné',
  T_SHOP_PLACE_STATUS_PCHECK: 'Kontrola výroby',
  T_SHOP_PLACE_STATUS_WCHECK: 'Kontrola tovaru',
  T_SHOP_PLACE_STATUS_WAIT: 'Čakaj',
  T_SHOP_PLACE_STATUS_MANUAL: 'Manuálne',

  T_SHOP_TT_LOCK_COMMENT: 'Komentár blokovania',
  T_SHOP_TT_LOAD_NO: 'Č. záťaže',
  T_SHOP_TT_IS_ON_PLACE: 'nachádza sa na mieste',
  T_SHOP_TT_PLACE: 'Miesto',
  T_SHOP_TT_BACK_TO_PLACE: 'Návrat na pracovisko č.',
  T_SHOP_TT_PRIORITY: 'Priorita',
  T_SHOP_TT_IS_RESERVED_FROM: 'rezervované od',
  T_SHOP_TT_TEST_LOAD: 'Testovacia záťaž',
  T_SHOP_TT_LONG_LOAD: 'Dlhá záťaž',
  T_SHOP_TT_PF_NO: 'Č. procesu',
  T_SHOP_TT_RECIPE: 'Recept',
  T_SHOP_TT_PLANNED: 'Naplánované',
  T_SHOP_TT_STARTED: 'Spustené',
  T_SHOP_TT_PREPARE_FROM: 'Pripraviť od',
  T_SHOP_TT_STARTING_TIME: 'Čas začiatku',
  T_SHOP_TT_ENDING_TIME: 'Čas ukončenia',
  T_SHOP_TT_STEP_NO: 'Č. kroku',
  T_SHOP_TT_PLANNING_SORTING: 'Triedenie plánu (1-99)',
  T_SHOP_TT_PF_COMPLETED: 'Proces ukončený',
  T_SHOP_TT_EMPTY_RACK: 'Prázdny stojan',
  T_SHOP_TT_NO_INFO: '---',
  T_SHOP_TT_ORDER: 'Zákazka',
  T_SHOP_TT_SERIAL_NO: 'Sériové č.',
  T_SHOP_TT_PART_DES: 'Popis dielu',
  T_SHOP_TT_PART_NO: 'Č. dielu',
  T_SHOP_TT_CUSTOMER: 'Zákazník',
  T_SHOP_TT_IN_STAGE: 'v etape:',

  T_SHOP_PF_STATUS_FREE: 'Voľné',
  T_SHOP_PF_STATUS_OCCUPIED: 'obsadené',
  T_SHOP_PF_STATUS_FINISHED: 'Ukončené',
  T_SHOP_PF_STATUS_ERROR: 'Chyba',
  T_SHOP_PF_STATUS_LOCKED: 'ZABLOKOVANÉ',
  T_SHOP_PF_STATUS_RESERVED: 'Rezervované',
  T_SHOP_PF_STATUS_LOCKED_HIGH_LOAD: 'Zablokované z dôvodu vysokého zaťaženia',
  T_SHOP_PF_STATUS_OILY: 'olejnaté',
  T_SHOP_PF_STATUS_WAIT_FOR_PLC: 'Čakanie na PLC',
  T_SHOP_PF_STATUS_EMPTY_RACK: 'Prázdny stojan',
  T_SHOP_PF_STATUS_EMPTY_RACK_ANNEALING_GOODS: 'Prázdny stojan pre výrobky na žíhanie',
  T_SHOP_PF_STATUS_BASE_TRAY_ANNEALING_GOODS: 'Základný stojan pre výrobky na žíhanie',
  T_SHOP_PF_STATUS_EMPTY_RACK_STAPLE_GOODS: 'Prázdny stojan pre balíkový tovar',
  T_SHOP_PF_STATUS_BASE_TRAY_PRODUCTS: 'Základný stojan pre výrobky',
  T_SHOP_PF_STATUS_TEST_LOAD: 'Testovacia záťaž',
  T_SHOP_PF_STATUS_BOTTOM_OILY: 'olejnaté zdola',
  T_SHOP_PF_STATUS_CHECK: 'Kontrola',
  T_SHOP_PF_STATUS_PROCESS_CHECK: 'Kontrola procesu',
  T_SHOP_PF_STATUS_WASCH_CHECK: 'Kontrola umývania',
  T_SHOP_PF_STATUS_WAIT: 'Čakanie',
  T_SHOP_PF_STATUS_MANUAL: 'Manuálne',

  T_SHOP_PF_STATUS_RESERVED_TEXT: 'Rezervované od ',
  T_SHOP_PF_STATUS_RESERVED_TEXT_LOAD: 'Číslo záťaže:',

  T_SHOP_PF_PRIORITY_NORMAL: 'Normálna',
  T_SHOP_PF_PRIORITY_HURRIED: 'Urgentná',
  T_SHOP_PF_PRIORITY_VERY_FAST: 'Veľmi rýchla',
  T_SHOP_PF_PRIORITY_AS_FAST_AS_POSSIBLE: 'Čo najrýchlejšie',

  T_SHOP_PF_LEGEND_TEST_PASS: 'Schválené',
  T_SHOP_PF_LEGEND_CLEANED: 'Vyčistené',
  T_SHOP_PF_LEGEND_PRE_OXIDIZED: 'Predbežne oxidované',
  T_SHOP_PF_LEGEND_PRE_HEATED: 'Predhriate',
  T_SHOP_PF_LEGEND_TEMPERED: 'Popúšťané',
  T_SHOP_PF_LEGEND_UNLOAD: 'Vyložiť',
  T_SHOP_PF_LEGEND_EMPTY_RACK: 'Prázdny stojan',
  T_SHOP_PF_LEGEND_LOCKED: 'Zablokované',
  T_SHOP_PF_LEGEND_DELAYED_START: 'Čas začiatku',
  T_SHOP_PF_LEGEND_PREHEATING: '->Predhrievanie',
  T_SHOP_PF_LEGEND_TO_IQ: '->IQ',
  T_SHOP_PF_LEGEND_TO_DROPPING: '->Vypúšťanie',
  T_SHOP_PF_LEGEND_TO_COOLING: '->Chladenie',
  T_SHOP_PF_LEGEND_TO_WASCHING: '->Umývanie',
  T_SHOP_PF_LEGEND_TO_TEMPERING: '->Popúšťanie',
  T_SHOP_PF_LEGEND_LOAD_IN_PROGRESS: 'Záťaž prebieha',
  T_SHOP_PF_LEGEND_IN_PROCESS_FLOW: 'V procese',
  T_SHOP_PF_LEGEND_TEST_LOAD: 'Testovacia záťaž',
  T_SHOP_PF_LEGEND_FOR_WASHING: 'Na umývanie',
  T_SHOP_PF_LEGEND_FOR_NITRIDING: 'Na nitridáciu',
  T_SHOP_PF_LEGEND_TEST_CHARGE_IN_PROGRESS: 'Testovacia záťaž prebieha',
  T_SHOP_PF_LEGEND_INCOMPLETE: 'Neúplné',
  T_SHOP_PF_LEGEND_TO_PACKAGING: 'Na balenie',
  T_SHOP_PF_LEGEND_FINISHED_LOAD: 'Záťaž ukončená',
  T_SHOP_PF_LEGEND_FINISHED_WITH_ALARMS: 'Záťaž ukončená s alarmami',
  T_SHOP_PF_LEGEND_WAIT: 'Čakaj',
  T_SHOP_PF_LEGEND_TEST_WAIT: 'Čakaj',
  T_SHOP_PF_LEGEND_TEST_LOAD_URGENT: 'Urgentná testovacia záťaž',
  T_SHOP_PF_LEGEND_TEST_PROCESS_INSPECTION: 'Inšpekcia procesu',
  T_SHOP_PF_LEGEND_TEST_WASH_INSPECTION: 'Inšpekcia umývania',
  T_SHOP_PF_LEGEND_IN_BETWEEN: 'Medzi',
  T_SHOP_PF_LEGEND_RESERVED: 'Rezervované',
  T_SHOP_PF_LEGEND_GR_G: 'GR-G.',
  T_SHOP_PF_LEGEND_LR_G: 'LR-G.',
  T_SHOP_PF_LEGEND_GR_S: 'GR-S.',
  T_SHOP_PF_LEGEND_LR_S: 'LR-S.',

  T_SHOP_PF_MESSAGE_SECOND_LOAD_ASSIGNED: 'K tejto záťaži je priradená druhá záťaž – najprv zrušte priradenie',
  T_SHOP_PF_MESSAGE_CONFIRMATION_NEXT_STAGE: 'Naozaj chcete manuálne zastaviť túto etapu a spustiť nasledujúcu?',
  T_SHOP_PF_MESSAGE_WARNING_DANGEROUS: '!!! POZOR !!! Toto je veľmi nebezpečná operácia',
  T_SHOP_PF_MESSAGE_NOT_VALID: 'Neplatné',
  T_SHOP_PF_MESSAGE_STEP_MANIPULATION: 'Manipulácia krokom',
  T_SHOP_PF_MESSAGE_ERROR_PLACE: 'Chyba: Miesto:',
  T_SHOP_PF_MESSAGE_ERROR_PF: 'Chyba: Proces:',
  T_SHOP_PF_MESSAGE_IS_NOT_OCCUPIED: 'nie je obsadené',
  T_SHOP_PF_MESSAGE_HAS_NO_LOAD: 'nemá záťaž',
  T_SHOP_PF_MESSAGE_HAS_NO_STAGE: 'nemá etapu',
  T_SHOP_PF_MESSAGE_NO_FURTHER_LOADS_PERMITTED: 'Ďalšie záťaže nie sú povolené!',
  T_SHOP_PF_MESSAGE_SELECT_START_TIME: 'Vyberte čas štartu',
  T_SHOP_PF_MESSAGE_LOAD_READY_NEW_STEP: 'Záťaž pripravená. Znova aktivovať kvôli novému kroku?',
  T_SHOP_PF_MESSAGE_SELECT_PERSON_FOR_PACK: 'Vyberte osobu pre <balenie záťaže>',
  T_SHOP_PF_MESSAGE_MAY_NOT_BE_EMPTY: 'nesmie byť prázdne',
  T_SHOP_PF_MESSAGE_ENTER_BOX_NO: 'Zadajte číslo schránky!',
  T_SHOP_PF_MESSAGE_ENTER_VALID_BOX_NO: 'Vyberte platné číslo schránky (10..49)',
  T_SHOP_PF_MESSAGE_CANNOT_PICK_TALL_LOADS: 'Nemožno zdvihnúť vysoké záťaže',
  T_SHOP_PF_MESSAGE_BOOK_PROCESS_FLOW: 'Zaregistrovať proces',
  T_SHOP_PF_MESSAGE_SELECT_TRAY_CODE: 'Vyberte kód stojana (1..99)!',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_1: 'Vyberte poradie záťaže (1..700)',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_2: 'Vyberte poradie záťaže menšie ako 701!',
  T_SHOP_PF_MESSAGE_ENTER_VALID_TOOLING: 'Zadajte platné náradia',
  T_SHOP_PF_MESSAGE_WAIT_PLC_CHECK_PROGRESS: 'Čakajte prosím. Prebieha overovanie s PLC ...',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED: 'Cieľové miesto je už obsadené. Naozaj ho chcete prepísať?',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED_TRAY: 'Cieľové miesto je už obsadené prázdnym stojanom. Naozaj ho chcete prepísať?',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_DELETE: 'je obsadené → možno ho môžete vymazať.',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_LOAD_DELETE: 'je obsadené záťažou → možno ho môžete vymazať.',
  T_SHOP_PF_MESSAGE_OCCUPY_ALREADY_OCCIPIED: 'Miesto je už obsadené!',
  T_SHOP_PF_MESSAGE_OCCUPY_CONFIRM: 'Chcete označiť miesto ako obsadené?',
  T_SHOP_PF_MESSAGE_NO_PF_CONTENT_FOUND: 'Obsah toku procesu nebol nájdený',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION: 'Údaje na mieste, ale žiadny BIT OBSADENÝ! Odomknutie by znamenalo vymazanie. <Áno> == vykonať aj tak?',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION_LAST_WARNING: '!!!! Posledné VAROVANIE !!! odomknutie znamená, že VYMAŽETE údaje miesta   <Áno>==Vymazať?',
  T_SHOP_PF_MESSAGE_NO_RIGHTS_FOR_ACTION: 'Na vykonanie tejto akcie nemáte oprávnenie. Kontaktujte svojho administrátora.',

  T_SHOP_PF_COLUMN_OPERATION_LABEL: 'Operácia',
  T_SHOP_PF_COLUMN_WORKPLACE_LABEL: 'Pracovisko',
  T_SHOP_PF_COLUMN_RECIPE_LABEL: 'Recept',
  T_SHOP_PF_COLUMN_DESCRIPTION_LABEL: 'Popis',
  T_SHOP_PF_COLUMN_TIME_LABEL: 'Čas',
  T_SHOP_PF_COLUMN_TIME_TOOLTIP: 'Čas čakania na tomto pracovisku (min)',
  T_SHOP_PF_COLUMN_ACK_LABEL: 'P',
  T_SHOP_PF_COLUMN_ACK_TOOLTIP: 'Potvrdiť',
  T_SHOP_PF_COLUMN_AUTOSKIP_LABEL: 'A',
  T_SHOP_PF_COLUMN_AUTOSKIP_TOOLTIP: 'Automatické preskočenie',
  T_SHOP_PF_COLUMN_MANUAL_LABEL: 'H',
  T_SHOP_PF_COLUMN_MANUAL_TOOLTIP: 'Manuálny štart/stop kroku',
  T_SHOP_PF_COLUMN_QM_RELEVAT_LABEL: 'Q',
  T_SHOP_PF_COLUMN_QM_RELEVAT_TOOLTIP: 'Krok relevantný pre kvalitu (QM)',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_LABEL: 'F',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_TOOLTIP: 'Externé spracovanie',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_LABEL: 'P',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_TOOLTIP: 'Krok relevantný pre výrobné štatistiky',
  T_SHOP_PF_COLUMN_MOVE_STEP_LABEL: 'C',
  T_SHOP_PF_COLUMN_MOVE_STEP_TOOLTIP: 'Posunúť krok bližšie k ďalšiemu (automatické plánovanie)',
  T_SHOP_PF_COLUMN_PTIME_LABEL: 'PČas',
  T_SHOP_PF_COLUMN_PTIME_TOOLTIP: 'Čas plánovania',
  T_SHOP_PF_COLUMN_MIN_HOLD_LABEL: 'Min. čas',
  T_SHOP_PF_COLUMN_MIN_HOLD_TOOLTIP: 'Minimálny čas zadržania v recepte (min)',
  T_SHOP_PF_COLUMN_MAX_HOLD_LABEL: 'Max. čas',
  T_SHOP_PF_COLUMN_MAX_HOLD_TOOLTIP: 'Maximálny čas zadržania v recepte (min)',
  T_SHOP_PF_COLUMN_WISH_TIME_LABEL: 'Plánovaný čas',
  T_SHOP_PF_COLUMN_WISH_TIME_TOOLTIP: 'Cieľový čas plánovania (min)',
  T_SHOP_PF_COLUMN_STEPNO_FROM_LABEL: 'Prepojené od kroku',
  T_SHOP_PF_COLUMN_STEPNO_FROM_TOOLTIP: 'Č. kroku (od ktorého platí min/max čas)',
  T_SHOP_PF_COLUMN_STEPNO_TO_LABEL: 'Prepojené do kroku',
  T_SHOP_PF_COLUMN_STEPNO_TO_TOOLTIP: 'Č. kroku (do ktorého platí min/max čas)',
  T_SHOP_PF_COLUMN_TRANSFER_LABEL: 'Čas prenosu SP (sek)',
  T_SHOP_PF_COLUMN_TRANSFER_TOOLTIP: 'Čas prenosu SP v sekundách z predchádzajúceho kroku',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_LABEL: 'Plánovaný čas prenosu (min)',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_TOOLTIP: 'Plánovaný čas prenosu v minútach',
  T_SHOP_PF_COLUMN_DROP_TIME_LABEL: 'Čas zhodenia SP (sek)',
  T_SHOP_PF_COLUMN_DROP_TIME_TOOLTIP: 'Čas zhodenia SP v sekundách',
  T_SHOP_PF_COLUMN_START_LABEL: 'Štart',
  T_SHOP_PF_COLUMN_END_LABEL: 'Koniec',
  T_SHOP_PF_COLUMN_PLANNED_WP_LABEL: 'Naplánované pre pracovisko',
  T_SHOP_PF_COLUMN_STATUS_LABEL: 'Stav',
  T_SHOP_PF_COLUMN_CHECKED_LABEL: 'Skontrolované',
  T_SHOP_PF_COLUMN_TRANSFER_TIME_LABEL: 'Čas prenosu (sek)',
  T_SHOP_PF_COLUMN_SOAK_TIME_LABEL: 'Čas namáčania (meranie)',
  T_SHOP_PF_COLUMN_SOAK_TIME_TOOLTIP: 'Čas namáčania záťaže pre tento krok',
  T_SHOP_PF_COLUMN_TEMP_AFTER_LABEL: 'Teplota po plánovaní',
  T_SHOP_PF_COLUMN_TEMP_AFTER_TOOLTIP: 'Teplota inštalácie po naplánovaní kroku',
  T_SHOP_PF_COLUMN_PREPARE_START_LABEL: 'Štart prípravy',
  T_SHOP_PF_COLUMN_PREPARE_START_TOOLTIP: '(Plánovaný) čas začiatku prípravy',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_LABEL: 'Dynamická nastavená hodnota',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_TOOLTIP: 'Dynamická nastavená hodnota z kroku procesu',

  T_SHOP_PF_COLUMN_PART_POS_LABEL: 'Pozícia',
  T_SHOP_PF_COLUMN_PART_POS_TOOLTIP: 'Číslo pozície (#)',
  T_SHOP_PF_COLUMN_PART_PARTNO_TOOLTIP: 'Zadajte číslo dielu (alebo kliknite na zoznam)',
  T_SHOP_PF_COLUMN_PART_DESC_LABEL: 'Popis dielu:',
  T_SHOP_PF_COLUMN_PART_DESC_TOOLTIP: 'Popis dielu',
  T_SHOP_PF_COLUMN_PART_AMOUNT_LABEL: 'Množstvo',
  T_SHOP_PF_COLUMN_PART_AMOUNT_2_LABEL: 'Po kontrole objemu',
  T_SHOP_PF_COLUMN_PART_AMOUNT_TOTAL_WEIGHT_LABEL: 'Celková hmotnosť',
  T_SHOP_PF_COLUMN_PART_ORDER_LABEL: 'Objednávka',
  T_SHOP_PF_COLUMN_PART_ORDER_TOOLTIP: 'Zadajte číslo objednávky (alebo kliknite na zoznam)',
  T_SHOP_PF_COLUMN_PART_ORDER_SELECTION_LABEL: 'Odovzdanie',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL: 'Sériové č.',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL_2: 'Poznámka k objednávke',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_3: 'Pole‑3',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_4: 'Pole‑4',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_LABEL: 'ďalšie',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_TOOLTIP: 'Upraviť v okne',
  T_SHOP_PF_COLUMN_PART_TYPE_LABEL: 'Typ',
  T_SHOP_PF_COLUMN_PART_TYPE_TOOLTIP: 'Typ (Vlastný, Cudzí, Príjmy)',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_LABEL: '-> Otvoriť okno dielov',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_TOOLTIP: 'Spustiť základné údaje dielov',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_LABEL: 'Interné',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_TOOLTIP: 'ID objednávky (interné)',
  T_SHOP_PF_COLUMN_PART_PROBE_LABEL: 'Vzorka',
  T_SHOP_PF_COLUMN_PART_MATERIAL_LABEL: 'Materiál',
  T_SHOP_PF_COLUMN_PART_DRAWING_NO_LABEL: 'Č. výkresu',

  T_SHOP_PF_DIALOG_EDIT_CAPTION: 'Úprava procesu',

  T_SHOP_PF_MESSAGE_PLACE_ALREADY_RESERVED: 'Nové miesto je už rezervované – zmena nie je povolená',
  T_SHOP_PF_MESSAGE_NUMBER_USED_1: 'Číslo',
  T_SHOP_PF_MESSAGE_NUMBER_USED_2: 'sa už používa!',
  T_SHOP_PF_MESSAGE_NUMBER_USED_3: 'Číslo odmietnuté!',

  T_SHOP_PF_MESSAGE_NOT_COMPLETE_1: 'v etape',
  T_SHOP_PF_MESSAGE_NOT_COMPLETE_2: 'neúplné',

  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_1: 'Množstvo príliš malé → použite proces:',
  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_2: 'namiesto procesu:',

  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_1: 'Chyba záťaže: nenájdený proces pre diel',
  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_2: 'a',

  T_SHOP_PF_DIALOG_DELETE_CAPTION: 'Vymazať miesto',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_CAPTION: 'Potvrdenie vymazania',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_TEXT: 'Naozaj chcete vymazať všetky dáta miesta č. (PFlow + obsah)?',
  T_SHOP_PF_DIALOG_DELETE_PERSON_CAPTION: 'Vyberte osobu, ktorá vyložila záťaž',
  T_SHOP_PF_DIALOG_DELETE_PERSON_ERROR: 'Bez uvedenia osoby vykladania nie je možné miesto vymazať',
  T_SHOP_PF_DIALOG_DELETE_RUNNING_PERMISSION_ERROR: 'Nie je oprávnenie na vymazanie aktívnej záťaže',
  T_SHOP_PF_DIALOG_DELETE_PLACE_NOT_FOUND_ERROR: 'Chyba: miesto v DB (b_wp_aut) nenájdené:',

  T_SHOP_PF_DIALOG_MOVE_TEXT: 'Presunúť dáta z tohto miesta na miesto č.',

  T_SHOP_PF_DIALOG_MANUAL_COMMAND_CAPTION: 'Presunúť na miesto pomocou manuálneho príkazu',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_TEXT: 'Zadajte cieľové miesto (manuálny príkaz):',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_VALID: 'Chyba: miesto {0} nie je prípustné',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_UNLOAD: 'Žiadny manuálny príkaz pre miesta vyloženia',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_LOADING: 'Záťaž nie je možné presunúť na stanicu vyloženia',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_FOUND_B_WP_AUT: 'Chyba: miesto {0} nenájdené v databáze b_wp_aut',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_CANNOT_BE_GIVEN: 'Použitie zariadenia ako cieľového miesta pre manuálny príkaz nie je povolené. Použite prosím proces.',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_IS_NOT_OCCUPIED: 'Chyba: miesto {0} nie je obsadené',

  // Cancel manual command
  T_SHOP_PF_DIALOG_CANCEL_MANUAL_COMMAND: 'Naozaj chcete zrušiť manuálny príkaz pre auto ?',

  // Reactivate pf
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_TEXT: 'Znova aktivovať vymazaný ProcessFlowNo: (bez - : YYNNNNN (YY=Rok NNNNN=Číslo)',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_NOT_FOUND: 'ProcessFlowNo sa nenašlo',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_ALREADY_ON_PLACE: 'Toto ProcessFlowNo sa už nachádza na mieste',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_INPUT: 'ProcessFlowNo musí obsahovať presne 7 znakov (2=Rok 5=Číslo)',

  T_SHOP_PF_DIALOG_CAPTION_INFORMATION: 'Informácia',
  T_SHOP_PF_DIALOG_CAPTION_MOVE: 'Presunúť',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRMATION_OVERWRITE: 'Potvrdenie pred prepísaním',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRM: 'Prosím potvrďte',

  T_SHOP_PF_BTN_LOCK_CAPTION: 'Zablokovať',
  T_SHOP_PF_BTN_LOCK_TOOLTIP: 'Zablokovať miesto',
  T_SHOP_PF_BTN_LOCK_UNLOCK_CAPTION: 'Odblokovať',
  T_SHOP_PF_BTN_LOCK_UNLOCK_TOOLTIP: 'Odblokovať miesto',

  T_SHOP_PF_BTN_UNLOAD_CAPTION: 'Vyložiť',
  T_SHOP_PF_BTN_UNLOAD_TOOLTIP: 'Vyložiť',

  T_SHOP_PF_BTN_LOAD_PARTS_CAPTION: 'Načítať diely',
  T_SHOP_PF_BTN_LOAD_PARTS_TOOLTIP: 'Načítať diely',

  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_CAPTION: 'Zmeniť proces',
  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_TOOLTIP: 'Zmeniť proces',

  T_SHOP_PF_BTN_START_PROCESS_FLOW_CAPTION: 'Povoliť štart',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_TOOLTIP: 'Povoliť štart',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_CAPTION: 'Zmeniť čas štartu',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_TOOLTIP: 'Zmeniť čas štartu',

  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_CAPTION: 'Zadať proces',
  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_TOOLTIP: 'Zadať proces',

  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_CAPTION: 'Manuálne riadenie vozíka',
  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_TOOLTIP: 'Manuálne riadenie vozíka',

  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION: 'Zrušiť manuálny príkaz',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_TOOLTIP: 'Zrušiť manuálny príkaz',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION_TO: 'Zrušiť manuálny príkaz do',

  T_SHOP_PF_BTN_DELETE_PLACE_CAPTION: 'Vymazať miesto',
  T_SHOP_PF_BTN_DELETE_PLACE_TOOLTIP: 'Vymazať miesto',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_CAPTION: 'Vyložiť',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_TOOLTIP: 'Vyložiť',

  T_SHOP_PF_BTN_OCCUPY_CAPTION: 'Obsadiť',
  T_SHOP_PF_BTN_OCCUPY_TOOLTIP: 'Obsadiť',

  T_SHOP_PF_BTN_SHOW_LOADS_CAPTION: 'Zobraziť záťaže',
  T_SHOP_PF_BTN_SHOW_LOADS_TOOLTIP: 'Zobraziť záťaže',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_CAPTION: 'Reaktivovať proces',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_TOOLTIP: 'Reaktivovať vymazaný proces na prázdnom stojane',

  T_SHOP_PF_BTN_STEP_MANIPULATION_CAPTION: 'Manipulácia krokom',
  T_SHOP_PF_BTN_STEP_MANIPULATION_TOOLTIP: 'Manipulácia krokom',

  T_SHOP_PF_BTN_MOVE_TO_PLACE_CAPTION: 'Presunúť',
  T_SHOP_PF_BTN_MOVE_TO_PLACE_TOOLTIP: 'Presunúť',

  T_SHOP_PF_BTN_SAVE_IMAGE: 'Uložiť obrázok',
  T_SHOP_PF_BTN_SAVE_IMAGE_EXISTS: 'Obrázok dostupný',

  T_SHOP_PF_BTN_LOAD_TESTED_CAPTION: 'Dokončiť/upraviť test záťaže',
  T_SHOP_PF_BTN_LOAD_TESTED_TOOLTIP: 'Záťaž skontrolovaná',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_CAPTION: 'Spustiť test záťaže',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_TOOLTIP: 'Skontrolovať záťaž',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_CAPTION: 'Skontrolované OK',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_TOOLTIP: 'Medzikontrola bola vykonaná',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_CAPTION: 'Kontrola procesu OK',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_TOOLTIP: 'Kontrola procesu bola vykonaná',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_CAPTION: 'Kontrola umývania OK',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_TOOLTIP: 'Kontrola umývania bola vykonaná',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_CAPTION: 'Ukončiť čakanie',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_TOOLTIP: 'Čakanie ukončené',

  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_CAPTION: 'Spustenie/Zastavenie spracovania QA',
  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_TOOLTIP: 'Spustenie/Zastavenie spracovania QA',

  T_SHOP_PF_CHECKBOX_TEST_LOAD_CAPTION: 'Testovacia záťaž',
  T_SHOP_PF_CHECKBOX_TEST_LOAD_TOOLTIP: 'Testovacia záťaž',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_CAPTION: 'Ukončiť urgentný test',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_TOOLTIP: 'Ukončiť urgentný test',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_CAPTION: 'NIO test',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_TOOLTIP: 'NIO test',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_CAPTION: '..Spracovanie QA..',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_TOOLTIP: '..Spracovanie QA..',

  T_SHOP_PF_UNLOAD_SELECT_PLACE_CAPTION: 'Vyberte miesto vyloženia:',
  T_SHOP_PF_UNLOAD_ERROR_MAGAZINE_ONLY: 'Vyloženie je možné len na skladových miestach',
  T_SHOP_PF_UNLOAD_ERROR_INVALID_OPTION: 'Zadané neplatné miesto vyloženia!',
  T_SHOP_PF_UNLOAD_ERROR_ALREADY_TARGET: ' (Miesto je už cieľom miesta: {0})',
  T_SHOP_PF_UNLOAD_ERROR_CAR_APPROACHED: ' (Miesto je aktuálne cieľom vozidla)',

  T_SHOP_PF_TEST_DIALOG_TITLE: 'Záťaž testovaná',
  T_SHOP_PF_TEST_DIALOG_TEXT: 'Zadajte meno a čas',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE: 'Zadajte meno a čas',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE_CAPTION: 'Záťaž testovaná',
  T_SHOP_PF_TEST_DIALOG_INPUT_TEST_STARTED_TEXT: 'Spustený test záťaže {0}',
  T_SHOP_PF_TEST_DIALOG_ERROR_ACTION_NOT_POSSIBLE: 'Operácia nie je možná',
  T_SHOP_PF_TEST_DIALOG_ERROR_PLACE_NOT_FOUND: 'Chyba: miesto v databáze (b_wp_aut) nenájdené: {0}',

  T_SHOP_PF_TEST_DIALOG_CONFIRM_NOT_READY_CHECKED: 'Záťaž ešte nie je pripravená! Naozaj označiť ako testovanú?',
  T_SHOP_PF_TEST_ENTER_RESULT_DIALOG_TEXT: 'Chcete zadať výsledok testu?',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_PASSED: 'Test úspešný',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_NOT_PASSED: 'Test neúspešný',

  T_LOAD_STATUS_WAIT_FOR: 'Čakanie na',
  T_LOAD_STATUS_PLANNED_START: 'plánovaný štart',
  T_LOAD_STATUS_EXTERNAL_START: 'externý štart',
  T_LOAD_STATUS_NOT_READY: 'NEPRIPRAVENÉ!',
  T_LOAD_STATUS_CONT_MANUAL: 'Riadič v manuálnom režime',
  T_LOAD_STATUS_STEP_1: 'z',
  T_LOAD_STATUS_STEP_2: 'Krok č.',
  T_LOAD_STATUS_RECIPE_FINISHED: 'Recept ukončený',
  T_LOAD_STATUS_OCCUPIED: 'obsadené',
  T_LOAD_STATUS_WILL_START: 'čoskoro spustí',
  T_LOAD_STATUS_WAIT_FOR_RECIPE: 'Čakanie na recept',
  T_LOAD_STATUS_WAIT_FOR_LOAD: 'Čakanie na záťaž',
  T_LOAD_STATUS_GET_OUT: 'odjazd',
  T_LOAD_STATUS_RECIPE: 'Recept',
  T_LOAD_STATUS_WANT: 'chce',
  T_LOAD_STATUS_PF_COMPLETED: 'Tok procesu ukončený',
  T_LOAD_STATUS_LOCKED: 'zablokované',
  T_LOAD_STATUS_PRIORITY: 'Priorita',
  T_LOAD_STATUS_NOT_OCCUPIED: '! neobsadené !',
  T_LOAD_STATUS_NO_TAG: 'Žiadny tag',
  T_LOAD_STATUS_PREPARE: 'PRÍPRAVA',
  T_LOAD_STATUS_RESERVED_BY: 'Rezervované od',

  T_GENERAL_ERROR_NOT_IMPLEMENTED: 'Neimplementované',
  T_GENERAL_ERROR_UNSUPPORTED_ON_WORKPLACE: 'Nepodporované na pracovisku',
  T_GENERAL_ERROR_CAPTION: 'Chyba',
  T_GENERAL_WARNING_CAPTION: 'Upozornenie',

  T_GENERAL_PART_NAME_1: 'Diel č.',
  T_GENERAL_PART_NAME_2: 'Kaefig-Nr.',
  T_GENERAL_PART_NAME_3: 'Bosch-Ch.-Nr.',
  T_GENERAL_PART_NAME_4: 'Ident-Nr',

  T_LOAD_DIALOG_ERROR_NO_PARTS: 'Neboli zadané žiadne diely!',
  T_LOAD_DIALOG_ERROR_ORDER: 'Objednávka (poz.) \'{0}\' bola nájdená!',
  T_LOAD_DIALOG_ERROR_PLACE_STATE: 'Miesto musí byť obsadené, ale bez dát (== prázdne miesto).',
  T_LOAD_DIALOG_ERROR_PLACE_ALREADY_OCCUPIED: 'Miesto je už obsadené dátami.',
  T_LOAD_DIALOG_ERROR_NOT_OCCUPIED_OR_EMPTY: 'Miesto nie je obsadené alebo stojan je prázdny!',
  T_LOAD_DIALOG_ERROR_PLACE_LOCKED: 'Toto miesto je zablokované!',
  T_LOAD_DIALOG_ERROR_PF_FROM_ORDER_NOT_FOUND: 'Nenájdený tok procesu z objednávky, pfTemplateId={0}.',
  T_LOAD_DIALOG_ERROR_NO_BOX_NO_FREE: 'Žiadne voľné číslo Box --> Stlačte Command-Repair...',
  T_LOAD_DIALOG_ERROR_CREATING_LOAD: 'Chyba pri vytváraní novej Cell-Load',
  T_LOAD_DIALOG_ERROR_NO_LOAD_FOUND: 'Nenájdená záťaž na pracovisku {0}',
  T_LOAD_DIALOG_ERROR_NO_PART_INFO_FOUND: 'Nenájdené informácie o načítaných dieloch pre pracovisko!',
  T_LOAD_DIALOG_ERROR_LOAD_ALREADY_ENABLED: 'Záťaž bola už povolená na automatické načítanie!',
  T_LOAD_DIALOG_ERROR_NO_A_LD_TMT_HEAD: 'Chyba: nenájdený a_ld_tmt_head pre loadId={0}, miesto z {1}',
  T_LOAD_DIALOG_ERROR_NO_LOAD: 'Chyba: nenájdená záťaž na mieste {0}, a_ld.id={1}',

  T_GANNT_PLANNING_NO_PLANNING_INFO: 'Žiadne informácie o plánovaní nie sú dostupné',
  T_GANNT_PLANNING_NO_CAR_COMMAND: 'Žiadny príkaz pre vozík nie je dostupný',

  // BELT FURNACES
  // Loading dialog
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOUND: 'Nebolo možné určiť údaje o náklade.',
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOR_BELT: 'Povolené iba pre nabíjanie pásových pecí!',
  T_BELT_LOAD_START_ERROR_ORDER_ALREADY_ACTIVE: 'Nemožné, objednávka je už aktívna.',
  T_BELT_LOAD_START_ERROR_ONLY_ONE_ACTIVE_ORDER: 'Môže existovať len jedna AKTÍVNA objednávka.',
  T_BELT_LOAD_START_ERROR_LOAD_ALREADY_STARTED: 'Nemožné, šarža už bola spustená.',
  T_BELT_LOAD_START_ERROR_LOAD_WAITING_FOR_CONDITION: 'Nemožné, pretože už existuje spustená šarža po tejto šarži.',
  T_BELT_LOAD_START_ERROR_ACTION_NOT_ALLOWED_FOR_ORDERS: 'Akcia pre objednávky nie je povolená.',
  T_BELT_LOAD_START_NEW_STATUS_WAS_FORCED: 'Nový stav bol vynútený.',
  T_BELT_LOAD_START_EXECUTION_STOPPED: 'Vykonávanie bolo prerušené.',
  T_BELT_LOAD_START_ACTIVATE_ORDER_CONFIRMATION: 'Chcete túto objednávku aktivovať?',
  T_BELT_LOAD_START_LOAD_CONFIRMATION: 'Naozaj chcete spustiť šaržu?',
  T_BELT_LOAD_START_LOAD_CONDITION_NOT_REACHED_CONFIRMATION: 'Podmienky ešte neboli splnené. Ste si istí, že chcete vynútiť stav šarže?',
  T_BELT_LOAD_START_LOADING_CONFIRMATION: 'Naozaj chcete spustiť nakládanie?',
  T_BELT_LOAD_START_LOADING_ERROR_NOT_FOR_THIS_CHARGE: 'Nemožné s touto šaržou.',
  T_BELT_LOAD_FINISH_LOADING_DELETE_ORDER_CONFIRMATION: 'Naozaj chcete vymazať objednávku (so všetkými neštartovanými šaržami)?',
  T_BELT_LOAD_FINISH_LOADING_STOP_CONFIRMATION: 'Naozaj chcete zastaviť nakládanie?',

  // BATCH FURNACES
  T_BATCH_ERROR_CHAMBER_NOT_REMOTE_OR_ACTIVE: 'Komora nie je v diaľkovom alebo aktívnom režime ...',

  T_END: 'Koniec',
} satisfies Record<Tpt9800TrlTokens, string>;

const batchRecipeItemVersion: Partial<TComponentPT9800Keys<'batchRecipeItemVersion'>> = {
  correctionProfile: 'Profily korekcie',
  positions: 'Kroky',
};

const digitalInput: Partial<TComponentPT9800Keys<'digitalInput'>> = {
  useForEndControl: 'Použiť ako koncovú podmienku',
};

export default {
  batchRecipeItemVersion,
  beltRecipeItemHeader,
  digitalInput,
  furnaceAlarm,
  load,
  loadState,
  material,
  planLoadInput,
  plannedPartInput,
  processFlow,
  processFlowContent,
  processFlowListItem,
  processFlowManualCommand,
  pt9800Translations,
  pusherRecipeItemHeader,
  recipeRuntimeInfo,
  recipeItemHeader,
  recipeItemPosition,
  recipeItemPositionHeader,
  recipeListItem,
  setPointItem,
  WorkPlaceStatus,
  WorkPlaceStatusStatus,
};
